/**
 * The four-column PROJECTION of a price-limit row on screen (CU-869fkbjdh,
 * UX rethink § 3.1): Where · When · Floor · Ceiling. Each column means one
 * thing whatever the row is; the words come from
 * `@services/priceLimitsProjection` so the table, the drawer and the CSV
 * preview say the same.
 *
 * Nothing here knows about saving: the amount cells call `onCommit` and
 * the tab decides what to do with it (the same collection update as
 * always). Colours only from `dsColorNew`: PROTECTION in the accent tone
 * (`noteInfo`), the matrix link in the app blue, the unprotected-days
 * chip in the warn tone.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Chip, Link, Stack, TableCell, TextField, Tooltip, Typography } from '@mui/material'
import { AppBrandMark, AppValueChip } from '@components'
import { dsColorNew } from '@palette'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { OpenInNewOutlined as OpenInNewIcon, WarningAmberOutlined as WarningIcon } from 'Pages/DsRepricing/@components/icons'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { useAxisCatalog } from 'Pages/DsRepricing/Setup/@services'
import { inputSx, headCellSx, codeSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import {
	whereChips, whereTail, whenLines, ceilingNote, unprotectedBands, isBinding, fmtMoney,
} from 'Pages/DsRepricing/Setup/@services/priceLimitsProjection'

/** The Price matrix tab with one matrix selected (criterion 22; `?matrix=`, decision 3). */
export const matrixHref = (id) => `${DS_REPRICING_ROUTES.SETUP_PATH}?tab=price-matrix&matrix=${encodeURIComponent(id)}`

/**
 * Names for the codes a rule stores, from the tenant's own catalogues.
 * One hook for the tab; the cells receive `labels` as a prop so a CSV
 * preview can label the same way.
 */
export function useRuleLabels() {
	const office = useOfficeScope()
	const cat = useCategories()
	const axes = useAxisCatalog()
	return useMemo(() => {
		const macroName = new Map(cat.tree.map(m => [m.code, m.name]))
		const labels = {
			office: (code) => { const o = office.byCode.get(code); return o?.name && o.name !== code ? `${code} · ${o.name}` : code },
			group:  (code) => office.byCode.get(code)?.name || code,
			macro:  (code) => macroName.get(code) || code,
			franchise: (id) => axes.franchiseName(id),
			insurance: (id) => axes.insuranceName(id),
		}
		return { labels, offices: office.offices, groups: office.groups, byCode: office.byCode,
		         tree: cat.tree, allSipps: cat.allSipps, axes,
		         loading: office.loading || cat.loading || axes.loading }
	}, [office, cat, axes])
}

/** A header cell of the table; the theme paints it (navy, white text). Sortable when asked. */
export function HeadCell({ children, sortable, active, dir, onSort, sx, ...rest }) {
	return (
		<TableCell {...rest}
		           onClick={sortable ? onSort : undefined}
		           aria-sort={sortable && active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
		           sx={{ ...headCellSx, py: 1, whiteSpace: 'nowrap', userSelect: 'none',
		                 cursor: sortable ? 'pointer' : 'default', ...sx }}>
			{children}
			{sortable && (
				<Box component="span" sx={{ ml: 0.5, fontSize: 10, opacity: active ? 1 : 0.6 }}>
					{active ? (dir === 'asc' ? '▲' : '▼') : '▲▼'}
				</Box>
			)}
		</TableCell>
	)
}

/** The franchise as its brand mark and name — the only chip that carries a logo. */
function FranchiseChip({ text }) {
	return (
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 24, pl: 0.75, pr: 1,
		                            borderRadius: '6px', border: `1px solid ${dsColorNew.grayBorder}`, bgcolor: dsColorNew.white,
		                            fontSize: 12, color: dsColorNew.textTitle, whiteSpace: 'nowrap' }}>
			<AppBrandMark name={text}/>{text}
		</Box>
	)
}

/** Where — chips per non-wildcard facet, in a fixed order; grey text for what stays "any". */
export function WhereCell({ rule, labels }) {
	const chips = whereChips(rule, labels)
	const tail = whereTail(rule)
	const hasBrand = chips.some(c => c.kind === 'franchise')
	return (
		<Stack direction="row" gap={0.625} alignItems="center" flexWrap={hasBrand ? 'wrap' : 'nowrap'}
		       sx={{ minWidth: 0, lineHeight: 1.3 }}>
			{chips.map((c, i) => (
				c.kind === 'sipp' ? <Box key={i} component="span" sx={codeSx}>{c.text}</Box>
				: c.kind === 'franchise' ? <FranchiseChip key={i} text={c.text}/>
				: <AppValueChip key={i}>{c.text}</AppValueChip>
			))}
			{tail && (
				<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textSubtle, whiteSpace: 'nowrap' }}>
					{tail}
				</Typography>
			)}
		</Stack>
	)
}

/** When — the reading as a word in small caps, the dates beneath. PROTECTION in the accent tone. */
export function WhenCell({ rule }) {
	const w = whenLines(rule)
	const prot = w.kind === 'PROTECTION'
	const color = prot ? dsColorNew.noteInfo : undefined
	return (
		<Stack gap={0.125} sx={{ lineHeight: 1.3 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
			                  color: prot ? dsColorNew.noteInfo : dsColorNew.textMuted }}>
				{w.title}
			</Typography>
			<Typography sx={{ fontSize: 13, color: color || dsColorNew.textTitle, fontWeight: prot ? 500 : 400 }}>
				{w.dates}
			</Typography>
		</Stack>
	)
}

/** The amber "N–30 days unprotected" chip, the full sentence in `title` (criterion 16). */
export function UnprotectedChip({ rule, matrix, sx }) {
	const u = unprotectedBands(rule, matrix)
	if (!u) return null
	return (
		<Chip size="small" icon={<WarningIcon sx={{ fontSize: 13 }}/>}
		      label={u.chip} title={u.text} aria-label={u.text}
		      sx={{ ...toneChipSx('warn'), height: 22, fontSize: 11, fontWeight: 600, ...sx }}/>
	)
}

const amountInput = (width) => ({ style: { width, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }, min: 0, step: '0.5' })

/**
 * A number that commits on blur (and Enter), not on every keystroke:
 * the collection saves 600 ms after a change and a half-typed "2" of
 * "25" must not travel. A `NaN` goes back to the value; `onCommit` only
 * fires when the number changed.
 */
export function AmountField({ value, onCommit, placeholder = '', aria, width = 72, disabled = false, sx, inputSx: extraInputSx }) {
	const [text, setText] = useState(value == null ? '' : String(value))
	useEffect(() => { setText(value == null ? '' : String(value)) }, [value])
	const commit = () => {
		const v = text.trim() === '' ? null : Number(text)
		if (Number.isNaN(v)) { setText(value == null ? '' : String(value)); return }
		if (v !== (value ?? null)) onCommit(v)
	}
	return (
		<TextField type="number" size="small" value={text} placeholder={placeholder} disabled={disabled}
		           inputProps={{ ...amountInput(width), 'aria-label': aria }}
		           onChange={(e) => setText(e.target.value)}
		           onBlur={commit}
		           onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
		           sx={{ ...inputSx, '& .MuiOutlinedInput-root': { height: 32, bgcolor: dsColorNew.white }, ...extraInputSx, ...sx }}/>
	)
}

/**
 * Floor — an amount editable in the cell (saved on blur like the rest of
 * Setup) OR the matrix name as a link that opens that matrix (SPA, never
 * an `<a href>`, M8). `value` may differ from `rule.min_price` after a
 * refusal (M9, option A: the cell shows the server's amount).
 */
export function FloorCell({ rule, matrix, value, onCommit, readOnly = false }) {
	if (isBinding(rule)) {
		const nAcriss = matrix ? 1 + (matrix.rules || []).length : null
		const nBands = matrix ? (matrix.base || []).length : null
		return (
			<Stack gap={0.25} alignItems="flex-end">
				<Link component={RouterLink} to={matrixHref(rule.matrix_id)}
				      title={matrix ? `Open the matrix ${matrix.name}` : 'Open the matrix'}
				      sx={{ color: dsColorNew.appBlue, fontWeight: 600, fontSize: 13, textDecoration: 'none',
				            display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
				            '&:hover': { textDecoration: 'underline' } }}>
					{matrix?.name || 'Matrix…'}
					<OpenInNewIcon sx={{ fontSize: 13 }}/>
				</Link>
				{/* Wraps inside its column: a nowrap line here bled into the When
				    column on the real tenant (Curro, 2026-10-20). */}
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, textAlign: 'right', lineHeight: 1.3 }}>
					per ACRISS × days{matrix ? ` · ${nAcriss} ACRISS · ${nBands} band${nBands === 1 ? '' : 's'}` : ''}
				</Typography>
				<UnprotectedChip rule={rule} matrix={matrix}/>
			</Stack>
		)
	}
	const shown = value === undefined ? rule.min_price : value
	if (readOnly) {
		return <Typography sx={{ fontWeight: 600, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(shown)}</Typography>
	}
	return (
		<Stack gap={0.25} alignItems="flex-end">
			<AmountField value={shown} aria={`Floor${rule.name ? ` of ${rule.name}` : ''}`} width={80}
			             onCommit={(v) => onCommit({ min_price: v })}/>
			<UnprotectedChip rule={rule} matrix={matrix}/>
		</Stack>
	)
}

/** Ceiling — an amount, or `—` with the reason in a tooltip that Tab reaches (criteria 5, 27). */
export function CeilingCell({ rule, value, onCommit, readOnly = false }) {
	const note = ceilingNote(rule)
	if (note) {
		return (
			<Tooltip title={note} arrow placement="left">
				<Box component="span" tabIndex={0} aria-label={note}
				     sx={{ display: 'inline-block', color: dsColorNew.textSubtle, fontSize: 14, pr: 1.75, cursor: 'help',
				           borderRadius: '6px', '&:focus-visible': { outline: `2px solid ${dsColorNew.webBlue500}`, outlineOffset: 1 } }}>
					—
				</Box>
			</Tooltip>
		)
	}
	const shown = value === undefined ? rule.max_price : value
	if (readOnly) {
		return <Typography sx={{ fontWeight: 600, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(shown)}</Typography>
	}
	return <AmountField value={shown} placeholder="—" aria="Ceiling" width={80} onCommit={(v) => onCommit({ max_price: v })}/>
}
